/**
 * Outil « Dichotomie » (Boîte à outils, 2nde → prépa).
 *
 * L'intervalle [a ; b] se coupe en deux à chaque étape : on garde la moitié
 * où f change de signe, f(a) × f(m) ≤ 0. Pas à pas en deux temps (calculer
 * le milieu m, puis tester le signe et garder une moitié), le programme
 * Python surligne les lignes exécutées au même moment ; une bande montre les
 * intervalles emboîtés, chacun moitié du précédent. « À toi » : l'élève
 * choisit la moitié d'après les signes (sans la courbe), l'outil explique.
 * Le texte suit le niveau : sans le TVI en 2nde-1re, corollaire du TVI en
 * Tle, preuve du TVI par suites adjacentes en prépa. Logique : `bisection.ts`.
 */

import { useEffect, useRef, useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { Note, PRIMARY_BUTTON, SECONDARY_BUTTON, Segmented, ToggleChip } from '../../ui/controls';
import { formatNumber, formatSci, frenchSpaces, superscript } from '../../ui/format';
import { FIGURE_BG_CLASS, HaloText } from '../../ui/svg-parts';
import {
  PROGRAM,
  after,
  cmp0,
  dec,
  decimalsOf,
  isDone,
  linesFor,
  outward,
  problemsFor,
  run,
  sgn,
  sign,
  step,
  stepsNeeded,
  type Phase,
  type Problem,
  type Step,
} from './bisection';

const PRECS = [0.1, 0.01, 0.001] as const;
const W = 320;
const H = 220;
const CURVE = 'var(--color-primary-500)';
const POS = 'var(--color-success-600)';
const NEG = 'var(--color-error-600)';
const NB = String.fromCharCode(0xa0);
const ZW = String.fromCharCode(0x200b);

const num = (v: number, d = 4) => formatNumber(v, d).replace('-', '−');
const precText = (v: number) => String(v).replace('.', ',');
/** espaces insécables autour de < > ≤ ≥ = ≈ : « f(2) > 0 » ne se coupe pas */
const tight = (s: string) => frenchSpaces(s).replace(/ ([<>≤≥=≈×−]) /g, `${NB}$1${NB}`);
/** « [1 ; 1,5] » */
const iv = (u: number, v: number) => `[${dec(u)} ; ${dec(v)}]`;

/** f(x) : « = 0,25 » si exact à 4 décimales, « ≈ 0,0127 », « ≈ 6,1 × 10⁻⁵ » si tout petit */
function valText(v: number): string {
  if (sgn(v) === 0) return '= 0';
  if (Math.abs(v) < 0.001) return `≈ ${formatSci(v, 1).replace('-', '−')}`;
  const t = num(v, 4);
  return Math.abs(Number(t.replace('−', '-').replace(',', '.')) - v) < 1e-12 ? `= ${t}` : `≈ ${t}`;
}

function Figure({
  p,
  a,
  b,
  m,
  zoom,
  curve,
}: {
  p: Problem;
  a: number;
  b: number;
  m: number | null;
  zoom: boolean;
  curve: boolean;
}) {
  const w = b - a;
  const x0 = zoom ? a - w / 4 : p.a - (p.b - p.a) / 4;
  const x1 = zoom ? b + w / 4 : p.b + (p.b - p.a) / 4;
  const ys: number[] = [p.f(a), p.f(b)];
  if (curve) for (let i = 0; i <= 200; i++) ys.push(p.f(x0 + ((x1 - x0) * i) / 200));
  let y0 = Math.min(0, ...ys);
  let y1 = Math.max(0, ...ys);
  const pad = (y1 - y0) * 0.15 || 1;
  y0 -= pad;
  y1 += pad;
  const tx = (x: number) => 14 + ((x - x0) / (x1 - x0)) * (W - 28);
  const ty = (y: number) => 10 + ((y1 - y) / (y1 - y0)) * (H - 20);
  const path = Array.from({ length: 241 }, (_, i) => {
    const x = x0 + ((x1 - x0) * i) / 240;
    return `${i ? 'L' : 'M'}${tx(x).toFixed(1)},${ty(p.f(x)).toFixed(1)}`;
  }).join(' ');
  const marks: Array<[number, string]> = [
    [a, 'a'],
    [b, 'b'],
    ...(m !== null ? ([[m, 'm']] as Array<[number, string]>) : []),
  ];
  const shown: number[] = [];
  const pt = ([x, label]: [number, string]) => {
    const y = p.f(x);
    const col = sgn(y) >= 0 ? POS : NEG;
    // étiquette masquée si elle tomberait sur une autre (sans zoom, a et m se touchent)
    const px = tx(x);
    const free = shown.every((s) => Math.abs(s - px) >= 16);
    if (free) shown.push(px);
    const below = sgn(y) >= 0;
    return (
      <g key={label}>
        <line x1={px} x2={px} y1={ty(0)} y2={ty(y)} stroke={col} strokeDasharray="3 3" />
        <circle cx={px} cy={ty(y)} r={4.5} fill={col} />
        {free && (
          <HaloText
            x={px}
            y={below ? ty(0) + 16 : ty(0) - 8}
            textAnchor="middle"
            fontSize={13}
            fontWeight={700}
            fill="var(--color-ink-900)"
          >
            {label}
          </HaloText>
        )}
      </g>
    );
  };
  const signs = `f(a) ${cmp0(p.f(a))}${m !== null ? `, f(m) ${cmp0(p.f(m))}` : ''}, f(b) ${cmp0(p.f(b))}`;
  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      className={cn(
        'border-ink-100 mx-auto block w-full max-w-[26rem] rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`${curve ? `Courbe de f(x) = ${p.spoken}` : 'Points de la courbe en a, m et b'} ; intervalle ${iv(a, b)}${m !== null ? ` ; milieu m = ${dec(m)}` : ''} ; ${signs}.`}
      data-testid="bs-figure"
    >
      <rect
        x={tx(a)}
        width={Math.max(1, tx(b) - tx(a))}
        y={0}
        height={H}
        fill="var(--color-success-500)"
        opacity={0.12}
      />
      <line x1={0} x2={W} y1={ty(0)} y2={ty(0)} stroke="var(--color-ink-400)" strokeWidth={1.25} />
      {curve && <path d={path} fill="none" stroke={CURVE} strokeWidth={3} data-testid="bs-curve" />}
      {marks.map(pt)}
    </svg>
  );
}

/** les intervalles emboîtés, à l'échelle de départ : chacun est la moitié du précédent */
function Nested({ p, steps, root }: { p: Problem; steps: Step[]; root: boolean }) {
  const rows: Array<[number, number]> = [[p.a, p.b], ...steps.map(after)];
  const RH = 9;
  const Hn = 14 + rows.length * RH;
  const tx = (x: number) => 10 + ((x - p.a) / (p.b - p.a)) * (W - 20);
  return (
    <svg
      viewBox={`0 0 ${W} ${Hn}`}
      className={cn(
        'border-ink-100 mx-auto block w-full max-w-[26rem] rounded-2xl border',
        FIGURE_BG_CLASS,
      )}
      role="img"
      aria-label={`Intervalles emboîtés : ${rows.length} intervalles, chacun moitié du précédent, le dernier ${iv(...rows.at(-1)!)}.`}
      data-testid="bs-nested"
    >
      {rows.map(([u, v], i) => (
        <rect
          key={i}
          x={tx(u)}
          width={Math.max(2, tx(v) - tx(u))}
          y={7 + i * RH}
          height={RH - 3}
          rx={2}
          fill={i === rows.length - 1 ? 'var(--color-primary-500)' : 'var(--color-primary-300)'}
        />
      ))}
      {root && (
        <line
          x1={tx(p.root)}
          x2={tx(p.root)}
          y1={2}
          y2={Hn - 2}
          stroke="var(--color-accent-600)"
          strokeWidth={1.5}
          strokeDasharray="3 3"
        />
      )}
    </svg>
  );
}

/** introduction selon le niveau */
function intro(p: Problem, rank: number): string {
  const fa = p.f(p.a);
  const fb = p.f(p.b);
  const ends = `f(${dec(p.a)}) ${cmp0(fa)} et f(${dec(p.b)}) ${cmp0(fb)}`;
  const named = p.rootName === 'α' ? '' : ` (ici α = ${p.rootName})`;
  if (rank <= 6)
    return `f(x) = ${p.text} sur [${dec(p.a)} ; ${dec(p.b)}] : ${ends}, et ${p.mono}. La courbe traverse donc l’axe des abscisses une seule fois : l’équation ${p.equation} a une seule solution α entre ${dec(p.a)} et ${dec(p.b)}${named}. On l’encadre de plus en plus près.`;
  const up = sgn(fb - fa) > 0;
  const tle = `f(x) = ${p.text} est continue et strictement ${up ? 'croissante' : 'décroissante'} sur [${dec(p.a)} ; ${dec(p.b)}] (${p.mono}), ${ends} : d’après le corollaire du théorème des valeurs intermédiaires, l’équation ${p.equation} a une unique solution α dans ]${dec(p.a)} ; ${dec(p.b)}[${named}.`;
  if (rank <= 7) return tle;
  return `${tle} La dichotomie prouve d’ailleurs le TVI : (aₙ) croît, (bₙ) décroît et bₙ − aₙ = (b − a)/2ⁿ → 0, ces suites sont adjacentes, de limite commune c ; comme f(aₙ) × f(bₙ) ≤ 0, par continuité f(c)² ≤ 0, donc f(c) = 0.`;
}

/** prévision du nombre d'étapes, sans logarithme : 2ⁿ ≥ (b − a)/p */
function planText(p: Problem, prec: number): string {
  const n = stepsNeeded(p, prec);
  const ratio = (p.b - p.a) / prec;
  const r = formatNumber(ratio, 3);
  return `À chaque étape l’amplitude b − a est divisée par 2 : après n étapes, elle vaut ${dec(p.b - p.a)}/2ⁿ. On s’arrête dès qu’elle est ≤ ${precText(prec)}, soit 2ⁿ ≥ ${r} : n = ${n} étapes (2${superscript(n - 1)} = ${2 ** (n - 1)} < ${r} ≤ ${2 ** n} = 2${superscript(n)}).`;
}

/** ce que fait une étape, en une phrase */
function stepText(s: Step, i: number): string {
  const [u, v] = after(s);
  if (s.exact)
    return `Étape ${i} : m = ${dec(s.m)} et f(m) = 0 : m est la solution exacte ! Le test f(a) × f(m) ≤ 0 est vrai, le programme garde ${iv(u, v)} (b prend la valeur ${dec(s.m)}) et continue tant que b − a > p.`;
  const prod = sgn(s.fa) * sgn(s.fm) <= 0 ? '≤ 0' : '> 0';
  return `Étape ${i} : m = ${dec(s.m)} ; f(a) ${cmp0(s.fa)} et f(m) ${cmp0(s.fm)}, donc f(a) × f(m) ${prod} : on garde ${s.keep === 'left' ? '[a ; m]' : '[m ; b]'} = ${iv(u, v)}, ${s.keep === 'left' ? 'b' : 'a'} prend la valeur ${dec(s.m)}.`;
}

export function BisectionTool({ level }: { level?: string | null }) {
  const rank = schoolLevelRank(level) ?? 7;
  const problems = problemsFor(rank);
  const [pid, setPid] = useState(problems[0]!.id);
  const p = problems.find((x) => x.id === pid) ?? problems[0]!;
  const [pi, setPi] = useState(1);
  const prec = PRECS[pi]!;
  const [mode, setMode] = useState<'auto' | 'choose'>('auto');
  const [steps, setSteps] = useState<Step[]>([]);
  const [phase, setPhase] = useState<'idle' | 'mid' | 'tested'>('idle');
  const [zoom, setZoom] = useState(true);
  const [feedback, setFeedback] = useState<{ ok: boolean; text: string } | null>(null);
  const [errors, setErrors] = useState(0);
  const [said, setSaid] = useState({ text: '', n: 0 });
  const endRef = useRef<HTMLParagraphElement>(null);
  const actionRef = useRef<HTMLButtonElement>(null);
  const focusEnd = useRef(false);
  const focusAction = useRef(false);

  const last = steps.at(-1) ?? null;
  const [a, b] = last ? after(last) : [p.a, p.b];
  const done = isDone(a, b, prec);
  const next = done ? null : step(p, a, b);
  const planned = stepsNeeded(p, prec);
  const showMid = !done && (mode === 'choose' || phase === 'mid');
  const ph: Phase = done
    ? 'done'
    : mode === 'choose' || phase === 'mid'
      ? 'mid'
      : phase === 'tested'
        ? 'tested'
        : 'start';
  const hot = linesFor(p, ph, last);
  const program = PROGRAM(p, String(prec));

  const say = (text: string) => setSaid((s) => ({ text, n: s.n + 1 }));
  useEffect(() => {
    if (focusEnd.current && done) endRef.current?.focus();
    if (focusAction.current) actionRef.current?.focus();
    focusEnd.current = false;
    focusAction.current = false;
  });

  const reset = () => {
    setSteps([]);
    setPhase('idle');
    setFeedback(null);
    setErrors(0);
  };
  const apply = (s: Step) => {
    const all = [...steps, s];
    setSteps(all);
    const [u, v] = after(s);
    if (isDone(u, v, prec)) {
      focusEnd.current = true;
      say(`Fini : ${iv(u, v)}, amplitude ${dec(v - u)}.`);
    }
    return all.length;
  };
  /** pas à pas : calculer m, puis tester et garder une moitié */
  const advance = () => {
    if (!next) return;
    if (phase !== 'mid') {
      setPhase('mid');
      say(`m = (a + b)/2 = ${dec(next.m)} ; f(m) ${valText(next.fm)}.`);
      return;
    }
    const n = apply(next);
    setPhase('tested');
    if (!isDone(...after(next), prec)) say(stepText(next, n));
  };
  const runAll = () => {
    setSteps(run(p, prec));
    setPhase('tested');
    focusEnd.current = true;
    const s = run(p, prec);
    const [u, v] = after(s.at(-1)!);
    say(`Fini en ${s.length} étapes : ${iv(u, v)}.`);
  };
  const choose = (k: 'left' | 'right') => {
    if (!next) return;
    const [u, v] = k === 'left' ? [next.a, next.m] : [next.m, next.b];
    let fb: { ok: boolean; text: string };
    if (next.exact)
      fb = {
        ok: true,
        text: `f(m) = 0 : m = ${dec(next.m)} est la solution exacte, elle est dans les deux moitiés. Le programme garde [a ; m] = ${iv(next.a, next.m)}, car son test est f(a) × f(m) ≤ 0.`,
      };
    else if (k === next.keep)
      fb = {
        ok: true,
        text: `Oui : f(${dec(u)}) ${cmp0(p.f(u))} et f(${dec(v)}) ${cmp0(p.f(v))}, f change de signe sur ${iv(u, v)} : ${k === 'left' ? 'b' : 'a'} prend la valeur ${dec(next.m)}.`,
      };
    else {
      setErrors((x) => x + 1);
      fb = {
        ok: false,
        text: `Non : f(${dec(u)}) et f(${dec(v)}) sont de même signe (${sign(p.f(u))} et ${sign(p.f(v))}). Comme f est strictement monotone, α n’est pas dans ${iv(u, v)} : garde la moitié où f change de signe.`,
      };
    }
    setFeedback(fb);
    say(fb.text);
    if (fb.ok) apply(next);
  };

  const restart = () => {
    reset();
    focusAction.current = true;
    say('On recommence.');
  };

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Fonction f" className="flex flex-wrap gap-1.5">
        {problems.map((x) => (
          <ToggleChip
            key={x.id}
            on={x.id === p.id}
            onClick={() => {
              setPid(x.id);
              reset();
            }}
          >
            <span aria-hidden>{x.label}</span>
            <span className="sr-only">{x.spoken}</span>
          </ToggleChip>
        ))}
      </div>
      <p className="text-ink-700 text-sm" data-testid="bs-intro">
        {tight(intro(p, rank))}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Segmented
          ariaLabel="Précision p"
          value={String(pi)}
          options={PRECS.map((v, i) => ({ value: String(i), label: `p = ${precText(v)}` }))}
          onChange={(v) => {
            setPi(Number(v));
            reset();
          }}
        />
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'auto', label: 'Pas à pas' },
            { value: 'choose', label: 'À toi' },
          ]}
          onChange={(v) => {
            setMode(v);
            reset();
          }}
        />
      </div>
      <p className="text-ink-600 text-xs" data-testid="bs-plan">
        {tight(planText(p, prec))}
      </p>

      <Figure
        p={p}
        a={a}
        b={b}
        m={showMid ? next!.m : null}
        zoom={zoom}
        curve={mode === 'auto' || done}
      />
      <div className="flex flex-wrap items-center gap-2">
        <ToggleChip on={zoom} onClick={() => setZoom(!zoom)}>
          {frenchSpaces('Zoomer sur [a ; b]')}
        </ToggleChip>
        <p className="text-ink-800 text-sm" data-testid="bs-state">
          {tight(
            `[a ; b] = ${iv(a, b)} : amplitude b − a = ${dec(b - a)} ${done ? '≤' : '>'} ${precText(prec)}${done ? ', on s’arrête.' : ', on continue.'}`,
          )}
        </p>
      </div>

      {mode === 'auto' && !done && (
        <div className="space-y-2">
          {showMid && next && (
            <p className="text-ink-900 text-sm" data-testid="bs-mid">
              {tight(
                `m = (a + b)/2 = ${dec(next.m)} ; f(a) ${cmp0(next.fa)} et f(m) ${valText(next.fm)}.`,
              )}
            </p>
          )}
          {!showMid && last && (
            <p className="text-ink-900 text-sm" data-testid="bs-step">
              {tight(stepText(last, steps.length))}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <button ref={actionRef} type="button" className={PRIMARY_BUTTON} onClick={advance}>
              {phase === 'mid' ? 'Tester et garder une moitié' : 'Calculer le milieu m'}
            </button>
            <button type="button" className={SECONDARY_BUTTON} onClick={runAll}>
              Tout dérouler
            </button>
          </div>
        </div>
      )}
      {mode === 'choose' && !done && next && (
        <div className="space-y-2">
          <p className="text-ink-900 text-sm font-semibold" data-testid="bs-question">
            {tight(
              `m = ${dec(next.m)}. f(a) = f(${dec(next.a)}) ${valText(next.fa)} ${cmp0(next.fa).slice(0, 1)} 0 et f(m) = f(${dec(next.m)}) ${valText(next.fm)}${next.exact ? '' : ` ${cmp0(next.fm).slice(0, 1)} 0`}. Dans quelle moitié f change-t-elle de signe ?`,
            )}
          </p>
          <div
            role="group"
            aria-label="Choisis la moitié à garder"
            className="flex flex-wrap gap-2"
          >
            <button
              ref={actionRef}
              type="button"
              className={SECONDARY_BUTTON}
              onClick={() => choose('left')}
            >
              {frenchSpaces(iv(next.a, next.m))}
            </button>
            <button type="button" className={SECONDARY_BUTTON} onClick={() => choose('right')}>
              {frenchSpaces(iv(next.m, next.b))}
            </button>
          </div>
        </div>
      )}
      {feedback && mode === 'choose' && (
        <Note live={false} tone={feedback.ok ? 'success' : 'error'}>
          <span data-testid="bs-feedback">{tight(feedback.text)}</span>
        </Note>
      )}
      {done && (
        <p
          ref={endRef}
          tabIndex={-1}
          className="bg-success-50 text-ink-800 rounded-2xl px-3 py-2 text-sm outline-none"
          data-testid="bs-end"
        >
          {tight(
            `Fini en ${steps.length} étape${steps.length > 1 ? 's' : ''}${steps.length === planned ? ', comme prévu' : ''}${mode === 'choose' ? ` (${errors} erreur${errors > 1 ? 's' : ''})` : ''}. ${dec(a)} ≤ α ≤ ${dec(b)} : encadrement d’amplitude ${dec(b - a)} ≤ ${precText(prec)}. ${dec(a)} est une valeur approchée de α à ${precText(prec)} près par défaut, ${dec(b)} par excès${p.rootName === 'α' ? '' : ` (α = ${p.rootName})`}. En arrondissant vers l’extérieur : ${outward(a, decimalsOf(prec) + 1, 'down')} ≤ α ≤ ${outward(b, decimalsOf(prec) + 1, 'up')}.`,
          )}
        </p>
      )}
      <button
        type="button"
        className={cn(SECONDARY_BUTTON, 'disabled:opacity-40')}
        onClick={restart}
        disabled={!steps.length && !feedback}
      >
        Recommencer
      </button>

      {/* une seule région annoncée, toujours présente ; ZW force la relecture d'un message répété */}
      <p className="sr-only" aria-live="polite" data-testid="bs-live">
        {said.text}
        {said.n % 2 ? ZW : ''}
      </p>

      {steps.length > 0 && (
        <section aria-label="Intervalles emboîtés" className="space-y-1">
          <p className="text-ink-500 text-xs font-semibold">
            {frenchSpaces(
              'Intervalles emboîtés, à l’échelle de départ : chacun est la moitié du précédent',
            )}
          </p>
          <Nested p={p} steps={steps} root={done} />
        </section>
      )}

      {steps.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-right text-sm tabular-nums" data-testid="bs-table">
            <caption className="text-ink-500 pb-1 text-left text-xs font-semibold">Étapes</caption>
            <thead className="text-ink-500 text-xs">
              <tr>
                <th scope="col" className="px-1 py-1 text-left font-semibold">
                  n
                </th>
                <th scope="col" className="px-1 py-1 font-semibold">
                  m
                </th>
                <th scope="col" className="px-1 py-1 font-semibold">
                  {frenchSpaces('f(a) × f(m)')}
                </th>
                <th scope="col" className="px-1 py-1 font-semibold">
                  nouvel intervalle
                </th>
              </tr>
            </thead>
            <tbody className="text-ink-800">
              {steps.map((s, i) => {
                const [u, v] = after(s);
                const prod = sgn(s.fa) * sgn(s.fm);
                return (
                  <tr key={i} className="border-ink-100 border-t">
                    <th scope="row" className="px-1 py-1 text-left font-semibold">
                      {i + 1}
                    </th>
                    <td className="px-1 py-1">{dec(s.m)}</td>
                    <td className="px-1 py-1">
                      <span
                        className={cn(
                          'text-ink-900 inline-block min-w-7 rounded-md px-1.5 text-center font-semibold',
                          prod > 0 ? 'bg-error-50' : 'bg-success-50',
                        )}
                      >
                        {prod === 0 ? '0' : prod > 0 ? '+' : '−'}
                      </span>
                    </td>
                    <td className="whitespace-nowrap px-1 py-1">{frenchSpaces(iv(u, v))}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <section aria-label="Programme Python" className="space-y-1">
        <p className="text-ink-500 text-xs font-semibold">
          {frenchSpaces('Le même algorithme en Python : lignes exécutées surlignées')}
        </p>
        <pre
          className="bg-ink-50 text-ink-900 overflow-x-auto rounded-xl p-2 text-xs leading-5"
          data-testid="bs-program"
        >
          {program.map((line, i) => {
            const on = hot.includes(i + 1);
            return (
              <div
                key={i}
                className={cn(
                  'border-l-4 px-1',
                  on ? 'bg-primary-100 border-primary-500 font-semibold' : 'border-transparent',
                )}
                data-hot={on || undefined}
              >
                <span
                  aria-hidden
                  className="text-ink-400 mr-2 inline-block w-4 select-none text-right"
                >
                  {i + 1}
                </span>
                {line || ' '}
                {on && <span className="sr-only"> (exécutée)</span>}
              </div>
            );
          })}
        </pre>
        <p className="text-ink-600 text-xs" data-testid="bs-program-note">
          {tight(
            done
              ? `b − a = ${dec(b - a)} ≤ ${precText(prec)} : la boucle s’arrête, le programme renvoie (${String(a)}, ${String(b)}).`
              : ph === 'mid'
                ? `Le test b − a > ${precText(prec)} est vrai : on calcule m = ${dec(next!.m)}.`
                : ph === 'tested'
                  ? `Après l’étape ${steps.length} : a = ${dec(a)}, b = ${dec(b)}.`
                  : `Au départ : a = ${dec(p.a)}, b = ${dec(p.b)}, p = ${precText(prec)}.`,
          )}
        </p>
      </section>
    </div>
  );
}
